import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Users,
  Wallet,
  TrendingUp,
  ShieldCheck,
  Search,
  Plus,
  ArrowLeftRight,
  FileText,
  Pencil,
  CalendarDays,
  Activity,
  ChevronRight,
} from "lucide-react";

import jsPDF from "jspdf";
import "jspdf-autotable";

import "../styles/investors.css";

import logo from "../assets/logo.png";

import NewInvestorModal from "../components/NewInvestorModal";
import TransactionModal from "../components/TransactionModal";
import EditInvestorModal from "../components/EditInvestorModal";

const API_BASE_URL =
  "https://client-management-backend-8x91.onrender.com";

const Investors = () => {
  const navigate = useNavigate();

  const [investors, setInvestors] = useState([]);

  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");

  const [isNewInvestorModalOpen, setIsNewInvestorModalOpen] =
    useState(false);

  const [isTransactionModalOpen, setIsTransactionModalOpen] =
    useState(false);

  const [editingInvestor, setEditingInvestor] =
    useState(null);

  useEffect(() => {
    fetchInvestors();
  }, []);

  const fetchInvestors = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `${API_BASE_URL}/investors`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch investors");
      }

      const data = await response.json();

      setInvestors(data);
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1200);
    }
  };

  const filteredInvestors = useMemo(() => {
    return investors.filter((investor) =>
      investor?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [investors, searchTerm]);

  const totalInvestors = investors.length;

  const totalCurrentBalance = investors.reduce(
    (sum, investor) =>
      sum +
      parseFloat(investor?.current_balance || 0),
    0
  );

  const totalAccountBalance = investors.reduce(
    (sum, investor) =>
      sum +
      parseFloat(investor?.account_balance || 0),
    0
  );

  const activeInvestors = investors.filter(
    (investor) =>
      investor?.status?.toLowerCase() === "active"
  ).length;

  const recentInvestors = [...filteredInvestors]
    .sort(
      (a, b) =>
        new Date(b.date_joined) -
        new Date(a.date_joined)
    )
    .slice(0, 5);

  const exportToPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
    });

    doc.setFontSize(18);

    doc.text("RCS Investor Summary Report", 14, 18);

    const tableData = filteredInvestors.map((inv) => [
      inv.name,
      inv.account_number || "N/A",
      inv.account_type,
      inv.status,
      inv.investment_term,
      `${parseFloat(inv.roi || 0).toFixed(2)}%`,
      `$${parseFloat(
        inv.current_balance || 0
      ).toFixed(2)}`,
      `$${parseFloat(
        inv.account_balance || 0
      ).toFixed(2)}`,
    ]);

    doc.autoTable({
      startY: 28,

      head: [
        [
          "Investor",
          "Account No.",
          "Type",
          "Status",
          "Term",
          "ROI",
          "Current Balance",
          "Account Balance",
        ],
      ],

      body: tableData,

      theme: "striped",

      styles: {
        fontSize: 9,
      },
    });

    doc.save("investors-report.pdf");
  };

  if (loading) {
    return (
      <div className="modern-investors-loading-screen">
        <div className="modern-loading-wrapper">
          <div className="modern-loading-ring"></div>

          <div className="modern-loading-logo-container">
            <img
              src={logo}
              alt="RCS Logo"
              className="modern-loading-logo"
            />
          </div>
        </div>

        <h2>Loading Investors</h2>

        <p>
          Preparing balances, ROI data and investor
          records...
        </p>
      </div>
    );
  }

  return (
    <div className="modern-investors-page">
      {/* HERO */}

      <div className="modern-investors-hero">
        <div className="modern-investors-left">
          <div className="modern-investors-tag">
            <Activity size={14} />
            Investor Management
          </div>

          <h1>
            Investor
            <br />
            Command Center.
          </h1>

          <p>
            Manage investor accounts, balances,
            returns, ROI and transaction performance
            from one modern dashboard.
          </p>

          <div className="modern-investors-actions">
            <button
              className="modern-primary-btn"
              onClick={() =>
                setIsNewInvestorModalOpen(true)
              }
            >
              <Plus size={18} />
              Add Investor
            </button>

            <button
              className="modern-secondary-btn"
              onClick={() =>
                setIsTransactionModalOpen(true)
              }
            >
              <ArrowLeftRight size={18} />
              Transaction
            </button>

            <button
              className="modern-secondary-btn"
              onClick={exportToPDF}
            >
              <FileText size={18} />
              Export
            </button>
          </div>
        </div>

        {/* PERFORMANCE */}

        <div className="modern-performance-card">
          <div className="modern-performance-header">
            <div>
              <h3>Performance</h3>
              <span>Investor Growth Overview</span>
            </div>

            <TrendingUp size={20} />
          </div>

          <div className="modern-performance-chart">
            <div className="chart-bars">
              <div className="bar one"></div>
              <div className="bar two"></div>
              <div className="bar three"></div>
              <div className="bar four"></div>
              <div className="bar five"></div>
              <div className="bar six"></div>
            </div>

            <div className="chart-line"></div>
          </div>

          <div className="modern-chart-footer">
            <div>
              <strong>
                $
                {totalCurrentBalance.toLocaleString()}
              </strong>

              <span>Current Managed Value</span>
            </div>

            <div className="growth-badge">
              +14.2%
            </div>
          </div>
        </div>
      </div>

      {/* STATS */}

      <div className="modern-investor-stats-grid">
        <div className="modern-stat-card">
          <div className="modern-stat-icon green">
            <Users size={22} />
          </div>

          <div>
            <p>Total Investors</p>
            <h2>{totalInvestors}</h2>
          </div>
        </div>

        <div className="modern-stat-card">
          <div className="modern-stat-icon teal">
            <Wallet size={22} />
          </div>

          <div>
            <p>Current Balance</p>

            <h2>
              $
              {totalCurrentBalance.toLocaleString()}
            </h2>
          </div>
        </div>

        <div className="modern-stat-card">
          <div className="modern-stat-icon gold">
            <TrendingUp size={22} />
          </div>

          <div>
            <p>Account Balance</p>

            <h2>
              $
              {totalAccountBalance.toLocaleString()}
            </h2>
          </div>
        </div>

        <div className="modern-stat-card">
          <div className="modern-stat-icon blue">
            <ShieldCheck size={22} />
          </div>

          <div>
            <p>Active Investors</p>

            <h2>{activeInvestors}</h2>
          </div>
        </div>
      </div>

      {/* LOOKUP */}

      <div className="modern-investor-toolbar">
        <div className="modern-search-box">
          <Search size={18} />

          <input
            type="text"
            placeholder="Lookup investor accounts..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(e.target.value)
            }
          />
        </div>
      </div>

      {/* RECENT */}

      <div className="modern-section-header">
        <div>
          <h3>Recent Active Accounts</h3>

          <span>
            Showing the 5 most recently active
            investor accounts, please use search to find additional accounts!
          </span>
        </div>
      </div>

      {/* CARDS */}

      <div className="modern-investor-cards-grid">
        {recentInvestors.map((investor) => (
          <div
            className="modern-investor-card"
            key={investor.id}
            onClick={() =>
              navigate(`/investor/${investor.id}`)
            }
          >
            <div className="modern-investor-card-top">
              <div className="modern-investor-avatar">
                {investor?.name?.charAt(0)}
              </div>

              <div className="modern-investor-main-info">
                <h4>{investor?.name}</h4>

                <span>
                  {investor?.account_number}
                </span>
              </div>

              <button
                className="modern-edit-btn"
                onClick={(e) => {
                  e.stopPropagation();

                  setEditingInvestor(investor);
                }}
              >
                <Pencil size={16} />
              </button>
            </div>

            <div className="modern-investor-card-body">
              <div className="modern-info-row">
                <span>Account Type</span>
                <strong>
                  {investor?.account_type}
                </strong>
              </div>

              <div className="modern-info-row">
                <span>Investment Term</span>
                <strong>
                  {investor?.investment_term}
                </strong>
              </div>

              <div className="modern-info-row">
                <span>ROI</span>
                <strong>
                  {parseFloat(
                    investor?.roi || 0
                  ).toFixed(2)}
                  %
                </strong>
              </div>

              <div className="modern-info-row">
                <span>Current Balance</span>

                <strong className="balance-green">
                  $
                  {parseFloat(
                    investor?.current_balance || 0
                  ).toLocaleString()}
                </strong>
              </div>
            </div>

            <div className="modern-investor-card-footer">
              <div className="modern-status-badge">
                {investor?.status}
              </div>

              <ChevronRight size={18} />
            </div>
          </div>
        ))}
      </div>

      {/* MODALS */}

      {isNewInvestorModalOpen && (
        <NewInvestorModal
          onClose={() =>
            setIsNewInvestorModalOpen(false)
          }
          refreshInvestors={fetchInvestors}
        />
      )}

      {isTransactionModalOpen && (
        <TransactionModal
          onClose={() =>
            setIsTransactionModalOpen(false)
          }
          refreshInvestors={fetchInvestors}
        />
      )}

      {editingInvestor && (
        <EditInvestorModal
          investor={editingInvestor}
          onClose={() =>
            setEditingInvestor(null)
          }
          refresh={fetchInvestors}
        />
      )}
    </div>
  );
};

export default Investors;